import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

interface QuestionOption {
  text: string;
  isCorrect: boolean;
}

interface Question {
  text: string;
  options: QuestionOption[];
  explanation?: string;
}

@Component({
  selector: 'app-custom-safety-test-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, MaxLengthDirective, CharCounterDirective],
  templateUrl: './custom-safety-test-form.component.html',
  styleUrls: ['./custom-safety-test-form.component.scss']
})
export class CustomSafetyTestFormComponent implements OnInit {
  testForm!: FormGroup;
  isEditMode = false;
  isLoading = false;
  isSaving = false;
  testId: string | null = null;

  // Icon options for custom tests
  iconOptions = [
    { value: 'bi-shield-check', label: 'Shield' },
    { value: 'bi-clipboard-check', label: 'Clipboard' },
    { value: 'bi-tools', label: 'Tools' },
    { value: 'bi-droplet-half', label: 'Droplet' },
    { value: 'bi-lightning', label: 'Lightning' },
    { value: 'bi-gear', label: 'Gear' },
    { value: 'bi-exclamation-triangle', label: 'Warning' },
    { value: 'bi-heart-pulse', label: 'Health' },
    { value: 'bi-fire', label: 'Fire' },
    { value: 'bi-thermometer-half', label: 'Temperature' },
    { value: 'bi-eye', label: 'Eye' },
    { value: 'bi-broadcast', label: 'Signal' }
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.testId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.testId;

    this.initializeForm();

    if (this.isEditMode && this.testId) {
      this.loadExistingTest();
    }
  }

  initializeForm(): void {
    this.testForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(5)]],
      shortName: ['', [Validators.required, Validators.maxLength(15)]],
      description: ['', [Validators.required, Validators.minLength(20)]],
      icon: ['bi-clipboard-check', Validators.required],
      passingScore: [80, [Validators.required, Validators.min(1), Validators.max(100)]],
      timeLimit: [30, [Validators.required, Validators.min(5), Validators.max(180)]],
      requiredAnnually: [false],
      shuffleQuestions: [true],
      showCorrectAnswers: [true],
      questions: this.fb.array([])
    });
  }

  get questions(): FormArray {
    return this.testForm.get('questions') as FormArray;
  }

  getQuestionOptions(questionIndex: number): FormArray {
    return this.questions.at(questionIndex).get('options') as FormArray;
  }

  addQuestion(): void {
    const questionGroup = this.fb.group({
      text: ['', Validators.required],
      explanation: [''],
      options: this.fb.array([
        this.createOption(true),  // First option is correct by default
        this.createOption(false),
        this.createOption(false),
        this.createOption(false)
      ])
    });
    this.questions.push(questionGroup);
  }

  createOption(isCorrect: boolean = false): FormGroup {
    return this.fb.group({
      text: ['', Validators.required],
      isCorrect: [isCorrect]
    });
  }

  removeQuestion(index: number): void {
    this.questions.removeAt(index);
  }

  moveQuestion(index: number, direction: 'up' | 'down'): void {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < this.questions.length) {
      const question = this.questions.at(index);
      this.questions.removeAt(index);
      this.questions.insert(newIndex, question);
    }
  }

  duplicateQuestion(index: number): void {
    const original = this.questions.at(index).value;
    const duplicate = this.fb.group({
      text: [original.text + ' (Copy)', Validators.required],
      explanation: [original.explanation],
      options: this.fb.array(
        original.options.map((opt: QuestionOption) => this.fb.group({
          text: [opt.text, Validators.required],
          isCorrect: [opt.isCorrect]
        }))
      )
    });
    this.questions.insert(index + 1, duplicate);
  }

  setCorrectAnswer(questionIndex: number, optionIndex: number): void {
    const options = this.getQuestionOptions(questionIndex);
    options.controls.forEach((control, i) => {
      control.patchValue({ isCorrect: i === optionIndex });
    });
  }

  addOption(questionIndex: number): void {
    const options = this.getQuestionOptions(questionIndex);
    if (options.length < 6) {
      options.push(this.createOption(false));
    }
  }

  removeOption(questionIndex: number, optionIndex: number): void {
    const options = this.getQuestionOptions(questionIndex);
    if (options.length > 2) {
      const wasCorrect = options.at(optionIndex).get('isCorrect')?.value;
      options.removeAt(optionIndex);
      // If we removed the correct answer, make the first option correct
      if (wasCorrect && options.length > 0) {
        options.at(0).patchValue({ isCorrect: true });
      }
    }
  }

  getCorrectOptionIndex(questionIndex: number): number {
    const options = this.getQuestionOptions(questionIndex);
    return options.controls.findIndex(opt => opt.get('isCorrect')?.value);
  }

  loadExistingTest(): void {
    this.isLoading = true;

    // Mock loading existing test data
    setTimeout(() => {
      const mockTest = {
        title: 'Company Equipment Safety Protocol',
        shortName: 'Equipment',
        description: 'Internal safety assessment covering company-specific equipment handling procedures, maintenance protocols, and safety guidelines for DJI Agras fleet.',
        icon: 'bi-tools',
        passingScore: 75,
        timeLimit: 30,
        requiredAnnually: false,
        shuffleQuestions: true,
        showCorrectAnswers: true,
        questions: [
          {
            text: 'What is the first step before powering on the DJI Agras T40?',
            explanation: 'Always perform a visual inspection before powering on any aircraft.',
            options: [
              { text: 'Conduct a visual inspection of the aircraft', isCorrect: true },
              { text: 'Connect the battery', isCorrect: false },
              { text: 'Start the remote controller', isCorrect: false },
              { text: 'Open the DJI Agras app', isCorrect: false }
            ]
          },
          {
            text: 'How often should propellers be inspected for damage?',
            explanation: 'Propellers should be inspected before every flight to ensure safe operation.',
            options: [
              { text: 'Once a week', isCorrect: false },
              { text: 'Before every flight', isCorrect: true },
              { text: 'Once a month', isCorrect: false },
              { text: 'Only after crashes', isCorrect: false }
            ]
          }
        ]
      };

      // Populate form
      this.testForm.patchValue({
        title: mockTest.title,
        shortName: mockTest.shortName,
        description: mockTest.description,
        icon: mockTest.icon,
        passingScore: mockTest.passingScore,
        timeLimit: mockTest.timeLimit,
        requiredAnnually: mockTest.requiredAnnually,
        shuffleQuestions: mockTest.shuffleQuestions,
        showCorrectAnswers: mockTest.showCorrectAnswers
      });

      // Load questions
      mockTest.questions.forEach(q => {
        const questionGroup = this.fb.group({
          text: [q.text, Validators.required],
          explanation: [q.explanation],
          options: this.fb.array(
            q.options.map(opt => this.fb.group({
              text: [opt.text, Validators.required],
              isCorrect: [opt.isCorrect]
            }))
          )
        });
        this.questions.push(questionGroup);
      });

      this.isLoading = false;
    }, 500);
  }

  onSubmit(): void {
    if (this.testForm.invalid) {
      this.testForm.markAllAsTouched();
      return;
    }

    if (this.questions.length < 1) {
      alert('Please add at least one question to the test.');
      return;
    }

    // Validate each question has a correct answer
    for (let i = 0; i < this.questions.length; i++) {
      const correctIndex = this.getCorrectOptionIndex(i);
      if (correctIndex === -1) {
        alert(`Question ${i + 1} must have a correct answer selected.`);
        return;
      }
    }

    this.isSaving = true;

    // Simulate API call
    setTimeout(() => {
      console.log('Saving custom test:', this.testForm.value);
      this.isSaving = false;
      this.router.navigate(['/logbook/testing/safety']);
    }, 1000);
  }

  onCancel(): void {
    this.router.navigate(['/logbook/testing/safety']);
  }

  get questionCount(): number {
    return this.questions.length;
  }

  get estimatedDuration(): string {
    const questions = this.questions.length;
    const timeLimit = this.testForm.get('timeLimit')?.value || 30;
    if (questions === 0) return 'N/A';
    const avgPerQuestion = Math.round(timeLimit / questions);
    return `~${avgPerQuestion} min per question`;
  }
}
